import React from 'react';
import { Box, VStack, Text, Icon, HStack, Circle, Grid, Divider, Flex, Spacer, useColorModeValue, List, ListItem, ListIcon, Badge } from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  TrendingUp, 
  DollarSign, 
  CheckCircle,
  Award,
  Shield,
  Briefcase,
  Lightbulb,
  Server,
  Code,
  Database,
  Users,
  Brain,
  Activity,
  Zap,
  Star,
  Clock,
  Lock,
  Globe,
  Target
} from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import { MethodistContentLayout } from '../../components/base/MethodistContentLayout';
import SlideContainer from '../../components/SlideContainer';

interface CostBenefitConclusionSlideProps {
  captionsText?: string;
  isSpeaking?: boolean;
  showCaptions?: boolean;
}

const CostBenefitConclusionSlide: React.FC<CostBenefitConclusionSlideProps> = ({
  captionsText = '',
  isSpeaking = false,
  showCaptions = true
}) => {
  // Key benefits of internal development
  const keyBenefits = [
    {
      title: "Financial Advantage",
      icon: DollarSign,
      color: "green.500",
      points: [
        "89% cost savings vs. agency development ($5K vs $45K)",
        "No recurring subscription fees unlike SaaS ($30K/year)",
        "Decreasing cost per user as platform scales"
      ]
    },
    {
      title: "Strategic Control",
      icon: Shield,
      color: "blue.500",
      points: [
        "Full intellectual property ownership",
        "Complete customization to Methodist's specific needs",
        "Ability to prioritize features based on clinician feedback"
      ]
    },
    {
      title: "Long-term Value",
      icon: TrendingUp,
      color: "purple.500",
      points: [
        "Platform evolves with Methodist's changing requirements",
        "Integration with existing Methodist systems",
        "Potential for research publications and grants"
      ]
    }
  ];

  // ROI highlights
  const roiHighlights = [
    {
      metric: "Physician Retention",
      value: "$175,000",
      icon: Users,
      description: "Cost saved per physician retained"
    },
    {
      metric: "Burnout Reduction",
      value: "40%",
      icon: Zap,
      description: "Projected decrease in burnout-related leaves"
    },
    {
      metric: "Productivity Gains",
      value: "$120,000",
      icon: Activity,
      description: "Annual value from improved efficiency"
    },
    {
      metric: "Implementation Time",
      value: "3-4 months",
      icon: Clock,
      description: "From development to deployment"
    }
  ];

  // Future opportunities
  const futureOpportunities = [
    {
      opportunity: "Expansion to Additional Departments",
      description: "Scale platform to support all clinical staff",
      icon: Globe
    },
    {
      opportunity: "Research & Publication",
      description: "Generate evidence-based outcomes data",
      icon: Lightbulb
    },
    {
      opportunity: "Enhanced AI Capabilities",
      description: "Integrate advanced LLM features as they emerge",
      icon: Brain
    },
    {
      opportunity: "Cross-Institutional Partnerships",
      description: "Potential for collaboration with other healthcare systems",
      icon: Briefcase
    }
  ];

  // Create a scrollbar style that's visible but not intrusive
  const scrollbarColor = useColorModeValue('rgba(0,0,0,0.3)', 'rgba(255,255,255,0.3)');
  const scrollbarBg = useColorModeValue('rgba(0,0,0,0.05)', 'rgba(255,255,255,0.05)');

  return (
    <MethodistSlide title="Cost-Benefit Analysis: Conclusion">
      <SlideContainer 
        captionsText={captionsText} 
        isSpeaking={isSpeaking}
        showCaptions={showCaptions}
        hudStyle="dark"
      >
        <VStack spacing={6} align="stretch">
          {/* Key Benefits of Internal Development */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            style={{
              padding: "1rem",
              borderRadius: "0.75rem",
              background: "white",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
            }}
          >
            <Text fontWeight="bold" fontSize="lg" mb={3}>Key Benefits of Internal Development</Text>
            <Grid templateColumns="repeat(3, 1fr)" gap={4}>
              {keyBenefits.map((benefit, index) => (
                <VStack 
                  key={index} 
                  align="start" 
                  p={3} 
                  borderRadius="md" 
                  bg={`${benefit.color.split('.')[0]}.50`}
                  borderLeft="4px solid"
                  borderColor={benefit.color}
                  h="100%"
                >
                  <HStack>
                    <Icon as={benefit.icon} color={benefit.color} />
                    <Text fontWeight="bold" fontSize="md">{benefit.title}</Text>
                  </HStack>
                  <List spacing={2} mt={2} w="100%">
                    {benefit.points.map((point, idx) => (
                      <ListItem key={idx} fontSize="sm">
                        <HStack align="start" spacing={2}>
                          <ListIcon as={CheckCircle} color={benefit.color} mt="3px" />
                          <Text>{point}</Text>
                        </HStack>
                      </ListItem>
                    ))}
                  </List>
                </VStack>
              ))}
            </Grid>
          </motion.div>

          {/* ROI Highlights */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.4 }}
            style={{
              padding: "1rem",
              borderRadius: "0.75rem",
              background: "white",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
            }}
          >
            <Text fontWeight="bold" fontSize="lg" mb={3}>Return on Investment Highlights</Text>
            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
              {roiHighlights.map((highlight, index) => (
                <VStack 
                  key={index} 
                  spacing={1} 
                  align="center" 
                  p={3} 
                  borderRadius="md" 
                  bg="gray.50"
                >
                  <Circle size="50px" bg="#0066CC" color="white">
                    <Icon as={highlight.icon} />
                  </Circle>
                  <Text fontWeight="bold" fontSize="md">{highlight.metric}</Text>
                  <Text fontSize="xl" fontWeight="bold" color="#0066CC">{highlight.value}</Text>
                  <Text fontSize="sm" textAlign="center">{highlight.description}</Text>
                </VStack>
              ))}
            </Grid>
          </motion.div>

          {/* Future Opportunities */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6 }}
            style={{
              padding: "1rem",
              borderRadius: "0.75rem",
              background: "white",
              boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)"
            }}
          >
            <Text fontWeight="bold" fontSize="lg" mb={3}>Future Opportunities</Text>
            <Grid templateColumns="repeat(4, 1fr)" gap={4}>
              {futureOpportunities.map((item, index) => (
                <HStack 
                  key={index} 
                  p={3} 
                  borderRadius="md" 
                  bg="gray.50"
                  borderLeft="4px solid"
                  borderColor="teal.400"
                  h="100%"
                  align="start"
                >
                  <Icon as={item.icon} color="teal.500" mt="2px" />
                  <VStack align="start" spacing={1}>
                    <Text fontWeight="bold" fontSize="sm">{item.opportunity}</Text>
                    <Text fontSize="xs">{item.description}</Text>
                  </VStack>
                </HStack>
              ))}
            </Grid>
          </motion.div>

          {/* Conclusion Banner */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.8 }}
            style={{
              padding: "0.75rem",
              borderRadius: "0.5rem",
              background: "#0066CC",
              color: "white"
            }}
          >
            <Flex justify="space-between" align="center">
              <HStack>
                <Icon as={Target} />
                <Text fontWeight="bold">Recommendation</Text>
              </HStack>
              <Text fontWeight="bold">
                Proceed with internal development of the JIMI Wellness Platform
              </Text>
              <Badge colorScheme="green" p={2} borderRadius="md">
                Highest ROI Option
              </Badge>
            </Flex>
          </motion.div>
        </VStack>
      </SlideContainer>
    </MethodistSlide>
  );
};

export default CostBenefitConclusionSlide;
